import {
  EnvelopeSimple,
  GoogleLogo,
  Monitor,
  SignIn,
  UserCircle,
  UserPlus,
} from '@phosphor-icons/react';
import { APP_NAME, authMessage, passwordProblem, LANGUAGE_NAME, LOCALES } from '@shh/shared';
import {
  Button,
  Heading,
  Icon,
  PasswordField,
  Surface,
  Text,
  TextField,
  buttonStyles,
  cn,
} from '@shh/ui';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  isSignInWithEmailLink,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
} from 'firebase/auth';
import { useEffect, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Link } from 'react-router';
import { friendlyError, sendResetMail, sendVerificationMail, takeGoodbye } from '../../data/api';
import { accountText } from '../../i18n/account';
import { InviteHint } from '../../components/InviteHint';
import { testFamilyText } from '../../i18n/admin';
import { readInvite } from '../../lib/invite';
import { firebase, useEmulators } from '../../lib/firebase';
import { locale, setDeviceLocale } from '../../lib/locale';
import { startText } from '../../i18n/start';
import { fill } from '../../i18n/fill';

/** Where the e-mail link of releases ≤ 1.14 stored the address (still read for open links). */
const EMAIL_KEY = 'shh.signin.email';

type Mode = 'signin' | 'signup' | 'reset';

/**
 * Sign in with Google or e-mail + password (password managers fill it, and unlike a mail link
 * it works inside the installed app on an iPhone). A wall display does not sign in at all –
 * it is paired with a code from a parent's phone.
 */
export function LoginPage() {
  const { auth } = firebase();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState<string>();
  // right after "Konto löschen": say goodbye once instead of only greeting
  const [goodbye] = useState(takeGoodbye);
  const [invited] = useState(() => Boolean(readInvite()));
  const [busy, setBusy] = useState(false);
  // Only complain about the password after the first try – not while someone is typing.
  const [tried, setTried] = useState(false);

  // An e-mail link sent before 1.15 still signs in (links live for a few hours/days).
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(EMAIL_KEY);
    } catch {
      /* private mode */
    }
    const address = stored ?? window.prompt(startText.emailPrompt);
    if (!address) return;
    void signInWithEmailLink(auth, address, window.location.href)
      .then(() => {
        try {
          localStorage.removeItem(EMAIL_KEY);
        } catch {
          /* ignore */
        }
        window.history.replaceState(null, '', '/');
      })
      .catch((e: unknown) =>
        setError(authMessage('auth/expired-action-code', locale) ?? String(e)),
      );
  }, [auth]);

  const switchTo = (next: Mode) => {
    setMode(next);
    setError(undefined);
    setTried(false);
    setResetSent(false);
  };

  const run = async (
    fn: () => Promise<unknown>,
    message: (e: unknown) => string | undefined = (e) =>
      authMessage((e as { code?: string }).code ?? '', locale),
  ) => {
    setError(undefined);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  const newPasswordProblem = mode === 'signup' ? passwordProblem(password, locale) : undefined;
  const address = email.trim();

  const submit = () => {
    setTried(true);
    if (mode === 'reset')
      // Our own mail (Resend, Larovi design) – the server never says whether the address exists.
      return run(async () => {
        await sendResetMail(address);
        setResetSent(true);
      }, friendlyError);
    if (mode === 'signup') {
      if (newPasswordProblem) return;
      return run(async () => {
        await createUserWithEmailAndPassword(auth, address, password);
        // the welcome mail with the confirmation link must never stand in the way of starting
        void sendVerificationMail().catch(() => undefined);
      });
    }
    return run(() => signInWithEmailAndPassword(auth, address, password));
  };

  /** Local development only: a ready test account, no e-mail needed. */
  const testLogin = () =>
    run(async () => {
      const mail = 'test@sweethomehub.local';
      await signInWithEmailAndPassword(auth, mail, 'test1234').catch(() =>
        createUserWithEmailAndPassword(auth, mail, 'test1234'),
      );
    });

  // Text-link look for switching views: a full button per option would crowd the card.
  const linkButton =
    'inline-flex min-h-touch-min items-center self-start rounded-control text-sm font-semibold text-text-link underline underline-offset-2 cursor-pointer outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus';

  return (
    <main className="mx-auto flex min-h-dvh max-w-narrow flex-col justify-center gap-section px-safe py-section">
      <header className="flex flex-col items-start gap-gap-md">
        <img src="/icon.svg" alt="" className="size-avatar-lg rounded-card" />
        <Heading level={1} size="xl">
          {startText.welcome(APP_NAME)}
        </Heading>
        <Text tone="secondary">{startText.intro}</Text>
      </header>
      {/* Two equal ways in (1.29, Mike 28.10.: "den Koppelmodus übersieht man dort unten schnell") –
          the shared device is the heart of the app, not a footnote under the language links. */}
      <Surface tone="sunken" className="flex flex-col gap-gap-sm">
        <span className="flex items-center gap-gap-sm">
          <Icon icon={Monitor} size="lg" className="text-accent-secondary" />
          <Heading level={2} size="sm">
            {startText.familyModeTitle}
          </Heading>
        </span>
        <Text tone="secondary" className="text-sm">
          {startText.familyModeText}
        </Text>
        <Link to="/koppeln" className={cn(buttonStyles({ variant: 'primary' }), 'self-start')}>
          {startText.asWall}
        </Link>
      </Surface>
      {invited && <InviteHint>{testFamilyText.invited}</InviteHint>}
      {goodbye && (
        <p
          role="status"
          className="flex items-start gap-gap-sm rounded-control bg-feedback-success-surface p-inset-sm font-semibold"
        >
          {accountText.goodbye}
        </p>
      )}

      <Surface className="flex flex-col gap-gap-lg">
        <Button
          size="lg"
          variant="secondary"
          icon={GoogleLogo}
          isDisabled={busy}
          onPress={() => run(() => signInWithPopup(auth, new GoogleAuthProvider()))}
        >
          {startText.google}
        </Button>

        <div className="flex items-center gap-gap-md text-sm text-text-muted" aria-hidden>
          <span className="h-px flex-1 bg-border-subtle" /> {startText.or}{' '}
          <span className="h-px flex-1 bg-border-subtle" />
        </div>

        <form
          aria-label={
            mode === 'signup'
              ? startText.signup
              : mode === 'reset'
                ? startText.forgot
                : startText.signin
          }
          className="flex flex-col gap-gap-md"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {mode !== 'signin' && (
            <Heading level={2} size="md">
              {mode === 'signup' ? startText.newAccount : startText.forgotAsk}
            </Heading>
          )}
          {mode === 'reset' && !resetSent && (
            <Text tone="secondary" className="text-sm">
              {startText.resetIntro}
            </Text>
          )}
          <TextField
            label={startText.email}
            type="email"
            autoComplete={mode === 'signup' ? 'email' : 'username'}
            value={email}
            onChange={setEmail}
            isRequired
          />
          {mode !== 'reset' && (
            <PasswordField
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              description={mode === 'signup' ? startText.min8 : undefined}
              value={password}
              onChange={setPassword}
              isRequired
              isInvalid={tried && !!newPasswordProblem}
              errorMessage={newPasswordProblem}
            />
          )}

          {resetSent ? (
            <p
              role="status"
              className="flex items-start gap-gap-sm rounded-control bg-feedback-success-surface p-inset-sm"
            >
              <Icon icon={EnvelopeSimple} size="md" className="text-feedback-success" />
              <span>
                {fill(startText.resetSent, <strong>{address}</strong>)}
                {useEmulators && startText.resetLocal}
              </span>
            </p>
          ) : (
            <Button
              type="submit"
              size="lg"
              icon={mode === 'signup' ? UserPlus : mode === 'reset' ? EnvelopeSimple : SignIn}
              isDisabled={busy || !address.includes('@') || (mode !== 'reset' && !password)}
            >
              {mode === 'signup'
                ? startText.signup
                : mode === 'reset'
                  ? startText.sendLink
                  : startText.signin}
            </Button>
          )}

          {error && (
            <p role="alert" className="text-sm font-semibold text-feedback-danger">
              {error}
            </p>
          )}

          <div className="flex flex-col">
            {mode === 'signin' && (
              <>
                <AriaButton className={linkButton} onPress={() => switchTo('reset')}>
                  {startText.forgotAsk}
                </AriaButton>
                <AriaButton className={linkButton} onPress={() => switchTo('signup')}>
                  {startText.newHere}
                </AriaButton>
              </>
            )}
            {mode !== 'signin' && (
              <AriaButton className={linkButton} onPress={() => switchTo('signin')}>
                {mode === 'signup' ? startText.haveAccount : startText.backToSignin}
              </AriaButton>
            )}
          </div>

          {mode === 'signin' && (
            <Text tone="secondary" className="text-sm">
              {startText.linkUsers}
            </Text>
          )}
        </form>

        {useEmulators && (
          <Button variant="quiet" icon={UserCircle} isDisabled={busy} onPress={testLogin}>
            {startText.testAccount}
          </Button>
        )}
      </Surface>

      <LanguageLinks />
    </main>
  );
}

/** The other languages as plain links – before signing in there are no settings yet. */
function LanguageLinks() {
  return (
    <nav aria-label="Sprache · Language" className="flex flex-wrap justify-center gap-gap-md">
      {LOCALES.filter((l) => l !== locale).map((l) => (
        <AriaButton
          key={l}
          lang={l}
          onPress={() => setDeviceLocale(l)}
          className="inline-flex min-h-touch-min cursor-pointer items-center rounded-control text-sm font-semibold text-text-link underline underline-offset-2 outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
        >
          {LANGUAGE_NAME[l]}
        </AriaButton>
      ))}
    </nav>
  );
}
